/**
 * inquiries — the lead inbox + its conversion into a project.
 *
 * DEMO-4: `useConvertInquiryToProject` used to hand back a throwaway
 * `crypto.randomUUID()` under Preview Studio, so the navigation that
 * follows it ("/studio/<id>") landed on "Project not found." The demo
 * branch now creates a real demo client + project + milestone set in
 * DemoStoreContext and links the inquiry to it, mirroring the live
 * path's idempotency. Nothing here writes to Supabase when isDemo.
 *
 * HOTFIX-2: the lead inbox (Index.tsx / NewDashboard.tsx) merges
 * TAGGED CLIENTS into the same table as real inquiries, giving them a
 * synthetic id of `client-<uuid>` and a hardcoded status of
 * 'accepted'. That made "Convert to Project" render for every tagged
 * client — and every click died on the very first query here, because
 * `inquiries.id` is a UUID column and `client-<uuid>` is not a UUID
 * (Postgres 22P02 → PostgREST 400). This is the founder-reported
 * "clients are failing to convert to Project". The live branch now
 * promotes a client-derived row into a real inquiry first (the same
 * promotion InquiryModal.handleSave already does), then converts it,
 * so both kinds of row take one code path.
 *
 * HOTFIX-2 also: `stageToStatus` mapped stage 'editing' to
 * 'post_production', which is NOT a member of the `project_status`
 * enum (discovery / pre_production / production / editing / review /
 * delivered) — every stage-sync write for a lead in Editing 400'd,
 * and the result was discarded, so it failed silently. And the
 * conversion now anchors on `projects.inquiry_id` so a half-finished
 * attempt can't leave an orphan project behind.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { useOrganization } from '@/services/organizations';
// DEMO-4 — `mockInquiries` / `getAnalyticsData` / `getServiceSummary`
// from mockData.ts are no longer imported: their names and dates were
// 2024-era ("Sarah Mitchell, submitted Jan 2024") and
// `getServiceSummary` padded its counts with Math.random(), so the
// same figure changed between renders mid-demo. `demoInquiries` is the
// only demo source now.
import { demoInquiries } from '@/data/demoOverlay';
import { Inquiry, InquiryStatus, ServiceType, StatsData, AnalyticsData, ServiceSummary, ProjectStage, FormResponse } from '@/types/crm';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import debug from '@/lib/debug';

type DBInquiry = Tables<'inquiries'>;

const formatServiceTypeAsInterest = (serviceType?: string | null): string => {
  if (!serviceType || serviceType === 'general') return 'General Inquiry';

  return serviceType
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// Map DB inquiry to UI Inquiry type
const mapDbToUi = (db: DBInquiry): Inquiry => ({
  id: db.id,
  name: db.name,
  email: db.email,
  phone: db.phone || undefined,
  interest: db.package || formatServiceTypeAsInterest(db.service_type),
  budget: '$0',
  status: db.status,
  projectStage: db.stage || 'lead',
  projectDate: db.project_date || undefined,
  location: db.location || undefined,
  packageSelected: db.package || undefined,
  projectDescription: db.description || undefined,
  heardAboutUs: db.how_heard || undefined,
  additionalNotes: db.notes || undefined,
  clientPortalEnabled: db.portal_enabled || false,
  portalUrl: db.portal_url || undefined,
  estimatedCompletion: db.estimated_completion || undefined,
  submittedAt: db.submitted_at,
  serviceType: db.service_type,
  projectId: db.project_id || undefined,
  formResponses: ((db as any).form_responses as FormResponse[] | null) || undefined,
  formTag: (db as any).form_tag || undefined,
});

// Map UI Inquiry to DB update type
const mapUiToDbUpdate = (ui: Partial<Inquiry>): TablesUpdate<'inquiries'> => ({
  name: ui.name,
  email: ui.email,
  phone: ui.phone || null,
  status: ui.status,
  stage: ui.projectStage,
  project_date: ui.projectDate || null,
  location: ui.location || null,
  package: ui.packageSelected || null,
  description: ui.projectDescription || null,
  how_heard: ui.heardAboutUs || null,
  notes: ui.additionalNotes || null,
  portal_enabled: ui.clientPortalEnabled,
  portal_url: ui.portalUrl || null,
  estimated_completion: ui.estimatedCompletion || null,
  service_type: ui.serviceType,
  project_id: ui.projectId || null,
  ...(ui.formTag !== undefined ? { form_tag: ui.formTag || null } : {}),
});

/**
 * Map a `project_stage` value onto a legal `project_status` value.
 *
 * HOTFIX-2 — every value on the right MUST be a member of the
 * `project_status` enum: discovery | pre_production | production |
 * editing | review | delivered (migration 20251206203951, line 4;
 * mirrored in src/integrations/supabase/types.ts). 'post_production'
 * was in here and is not one of them — Postgres rejected the write
 * with 22P02 and the caller threw the result away.
 *
 * The return type is pinned to the generated enum so the compiler —
 * not production — catches the next person who types a stage value
 * into this table.
 */
type ProjectStatus = NonNullable<TablesUpdate<'projects'>['status']>;

const stageToStatus = (stage: ProjectStage): ProjectStatus => {
  const mapping: Record<ProjectStage, ProjectStatus> = {
    lead: 'discovery',
    contacted: 'discovery',
    discovery_call: 'discovery',
    proposal_sent: 'pre_production',
    contract_sent: 'pre_production',
    booked: 'pre_production',
    in_progress: 'production',
    editing: 'editing',
    delivered: 'delivered',
    archived: 'delivered',
  };
  return mapping[stage] || 'discovery';
};

/**
 * UX-2 — ONE user-facing pipeline. The Inquiry Details UI now shows
 * only Status; the Project Stage dropdown is gone from the lead
 * surfaces. `project_stage` still exists and still matters (the
 * /projects kanban and iOS speak it), so these two mappings keep the
 * hidden stage — and, via `stageToStatus` above, the linked project's
 * status — consistent without ever surfacing a second state machine
 * on a lead.
 *
 * `stageToInquiryStatus` is the READ direction: which status bucket a
 * stage rolls up to. It used to live inline in InquiryModal's stage
 * handler; it's here now so both directions sit next to the enum
 * tables they must stay in sync with.
 *
 * `statusToStage` is the WRITE direction: the canonical stage a bare
 * status change lands on. Callers must only write
 * `statusToStage(status)` when `stageToInquiryStatus(currentStage)`
 * DISAGREES with the new status — that guard is what stops a no-op
 * re-save from clobbering a finer-grained stage (e.g. 'editing' under
 * In Progress, 'proposal_sent' under Follow Up) that was set from the
 * project board.
 *
 * Both Records are pinned to the full enums so the compiler — not
 * production — catches the next member that gets added to either side
 * (that's how the 'post_production' bug shipped; see HOTFIX-2 above).
 */
export const stageToInquiryStatus = (stage: ProjectStage): InquiryStatus => {
  const mapping: Record<ProjectStage, InquiryStatus> = {
    lead: 'pending',
    contacted: 'follow_up',
    discovery_call: 'follow_up',
    proposal_sent: 'follow_up',
    contract_sent: 'follow_up',
    booked: 'accepted',
    in_progress: 'in_progress',
    editing: 'in_progress',
    delivered: 'completed',
    archived: 'completed',
  };
  return mapping[stage] || 'pending';
};

export const statusToStage = (status: InquiryStatus): ProjectStage => {
  const mapping: Record<InquiryStatus, ProjectStage> = {
    pending: 'lead',
    follow_up: 'contacted',
    accepted: 'booked',
    in_progress: 'in_progress',
    completed: 'delivered',
    closed_lost: 'archived',
  };
  return mapping[status] || 'lead';
};

/**
 * HOTFIX-2 — leads merged in from the `clients` table carry a
 * synthetic `client-<uuid>` id. They are NOT rows in `inquiries`, so
 * anything that filters `inquiries.id` by one of these will 400.
 */
const CLIENT_DERIVED_PREFIX = 'client-';
export const isClientDerivedInquiryId = (id: string): boolean =>
  id.startsWith(CLIENT_DERIVED_PREFIX);

export const useInquiries = (serviceType?: ServiceType) => {
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  const { data: organization } = useOrganization();
  const orgId = organization?.id;

  return useQuery({
    queryKey: ['inquiries', orgId, serviceType, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async (): Promise<Inquiry[]> => {
      if (isDemo) {
        // Session-scoped store first so newly-added inquiries appear
        // immediately in the inbox; overlay seed if the store hasn't
        // hydrated (tests). DEMO-4 removed the legacy mock tail.
        const storeRows = demoStore.inquiries.list();
        const source = (
          storeRows.length > 0 ? storeRows : demoInquiries
        ) as unknown as Inquiry[];
        const filtered = serviceType
          ? source.filter((i: any) => i.serviceType === serviceType || i.service_type === serviceType)
          : source;
        return filtered;
      }

      if (!orgId) {
        debug.warn('useInquiries: No org_id available, returning empty array');
        return [];
      }

      debug.log('Fetching inquiries', { orgId, serviceType });

      let query = supabase
        .from('inquiries')
        .select('*')
        .eq('org_id', orgId);
      
      // Only filter by service_type if it's not 'all' (which means show everything)
      if (serviceType && serviceType !== 'all') {
        query = query.eq('service_type', serviceType);
      }

      const { data, error } = await query.order('submitted_at', { ascending: false });

      if (error) {
        debug.error({ action: 'fetchInquiries', orgId, table: 'inquiries' }, error);
        throw error;
      }
      
      debug.success('Fetched inquiries', { count: data?.length ?? 0 });
      return (data || []).map(mapDbToUi);
    },
    enabled: isDemo || !!orgId,
  });
};

export const useCreateInquiry = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  const { data: organization } = useOrganization();

  return useMutation({
    mutationFn: async (inquiry: TablesInsert<'inquiries'>) => {
      if (isDemo) {
        const now = new Date().toISOString();
        const created = {
          id: `demo-i-${crypto.randomUUID().slice(0, 8)}`,
          status: 'pending',
          submitted_at: now,
          submittedAt: now,
          created_at: now,
          projectStage: 'lead',
          ...inquiry,
        };
        return demoStore.inquiries.add(created);
      }

      const orgId = inquiry.org_id || organization?.id;
      
      if (!orgId) {
        // UX-8 — studio language; App.tsx's mutation onError matches
        // "studio isn't set up" and attaches the Set up action.
        const error = new Error('Your studio isn’t set up yet. Please complete your studio setup first.');
        debug.error({ action: 'createInquiry', table: 'inquiries', payload: inquiry }, error);
        throw error;
      }

      debug.log('Creating inquiry', { orgId, name: inquiry.name });

      const { data, error } = await supabase
        .from('inquiries')
        .insert({ ...inquiry, org_id: orgId })
        .select()
        .single();

      if (error) {
        debug.error({ action: 'createInquiry', orgId, table: 'inquiries', payload: inquiry }, error);
        throw new Error(debug.parseSupabaseError(error));
      }
      
      debug.success('Created inquiry', { id: data.id });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
};

export const useUpdateInquiry = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, updates, syncToProject = true }: { id: string; updates: Partial<Inquiry>; syncToProject?: boolean }) => {
      if (isDemo) {
        const merged = demoStore.inquiries.update(id, updates as any);
        return merged ?? { id, ...updates };
      }

      // HOTFIX-2 — a client-derived row (`client-<uuid>`) has no
      // inquiries row to update. Callers already guard this, but if
      // one slips through we say so instead of firing a 400 at a
      // UUID column.
      if (isClientDerivedInquiryId(id)) {
        throw new Error(
          "That entry is a client, not a lead — save it from the client's record instead.",
        );
      }

      const dbUpdates = mapUiToDbUpdate(updates);
      const { data, error } = await supabase
        .from('inquiries')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Sync status/stage to linked project if it exists.
      //
      // HOTFIX-2 — this write's error used to be discarded entirely.
      // The inquiry itself HAS saved by this point, so throwing would
      // make the caller roll its dropdowns back to a value the server
      // no longer holds. Instead we hand the failure back on the
      // result and let the caller warn about it. No silent drops.
      let projectSyncError: string | null = null;
      if (syncToProject && data.project_id && (updates.projectStage || updates.status)) {
        // Typed against the generated row shape so an unknown column
        // or an illegal enum member fails to compile instead of 400ing
        // at runtime — that's how the 'post_production' bug survived.
        const projectUpdates: TablesUpdate<'projects'> = {};

        if (updates.projectStage) {
          projectUpdates.stage = updates.projectStage;
          projectUpdates.status = stageToStatus(updates.projectStage);
        }

        if (Object.keys(projectUpdates).length > 0) {
          const { error: syncError } = await supabase
            .from('projects')
            .update(projectUpdates)
            .eq('id', data.project_id);

          if (syncError) {
            debug.error(
              {
                action: 'syncInquiryStageToProject',
                table: 'projects',
                payload: { id: data.project_id, ...projectUpdates },
              },
              syncError,
            );
            projectSyncError = debug.parseSupabaseError(syncError);
          }
        }
      }

      return { ...data, projectSyncError };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

export const useDeleteInquiry = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async (id: string) => {
      if (isDemo) {
        demoStore.inquiries.remove(id);
        return { id };
      }

      // HOTFIX-2 — same class as the conversion bug: the leads table
      // shows tagged clients under a synthetic `client-<uuid>` id, and
      // Delete fired straight at `inquiries.id` (a UUID column), so it
      // 400'd with an unreadable "invalid input syntax for type uuid".
      // A client isn't ours to delete from here.
      if (isClientDerivedInquiryId(id)) {
        throw new Error(
          "That row is a client, not a lead — remove it from Clients, or clear its category tag to take it off this list.",
        );
      }

      const { error } = await supabase.from('inquiries').delete().eq('id', id);

      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
};

// Convert inquiry to project
export const useConvertInquiryToProject = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async (inquiry: Inquiry) => {
      if (isDemo) {
        // DEMO-4 — this used to `return { projectId: crypto.randomUUID() }`.
        // InquiryModal then navigated to /studio/<that uuid>, nothing
        // resolved it, and ProjectWorkspace rendered "Project not
        // found." The single most-demoed action in the app was a dead
        // end. Preview Studio now creates REAL rows — in the demo
        // store, never in Supabase — so the navigation lands on a
        // populated workspace and the client shows up in Clients.
        //
        // Same idempotency contract as the live path below: converting
        // twice returns the existing pair instead of double-creating.
        const existingInquiry = demoStore.inquiries
          .list()
          .find((i) => i.id === inquiry.id);
        const alreadyLinked =
          (existingInquiry?.projectId as string | undefined) ??
          (existingInquiry?.project_id as string | undefined) ??
          inquiry.projectId;
        if (alreadyLinked) {
          const linked = demoStore.projects
            .list()
            .find((p) => p.id === alreadyLinked);
          if (linked) {
            return {
              projectId: linked.id as string,
              portalToken: (linked.portal_token as string | null) ?? null,
              clientId: (linked.client_id as string | null) ?? null,
            };
          }
          // Linked id with no row behind it — fall through and rebuild
          // rather than navigate somewhere empty.
        }

        // Reuse a demo client on an email match, same rule as live.
        const emailKey = (inquiry.email ?? '').trim().toLowerCase();
        let client = emailKey
          ? demoStore.clients
              .list()
              .find(
                (c) => String(c.email ?? '').trim().toLowerCase() === emailKey,
              )
          : undefined;
        if (!client) {
          client = demoStore.clients.add({
            id: `demo-c-${crypto.randomUUID().slice(0, 8)}`,
            name: inquiry.name,
            email: inquiry.email ?? null,
            phone: inquiry.phone ?? null,
            brand: null,
            notes: inquiry.additionalNotes ?? null,
            created_at: inquiry.submittedAt || new Date().toISOString(),
          });
        }

        const nowIso = new Date().toISOString();
        const projectId = `demo-p-${crypto.randomUUID().slice(0, 8)}`;
        const portalToken = `demo-portal-${crypto.randomUUID().slice(0, 12)}`;
        const stage = inquiry.projectStage || 'lead';
        const project = demoStore.projects.add({
          id: projectId,
          name: inquiry.projectDescription || `${inquiry.name} Project`,
          title: inquiry.projectDescription || `${inquiry.name} Project`,
          description: inquiry.projectDescription ?? null,
          client_id: client.id,
          clientId: client.id,
          client: { id: client.id, name: client.name },
          status: stageToStatus(stage as ProjectStage),
          stage,
          budget: null,
          value: null,
          type: inquiry.serviceType ?? 'other',
          project_type: inquiry.serviceType ?? 'other',
          location: inquiry.location ?? null,
          due_date: inquiry.estimatedCompletion || null,
          dueDate: inquiry.estimatedCompletion || null,
          scheduled_date: inquiry.projectDate || null,
          notes: inquiry.additionalNotes ?? null,
          portal_enabled: inquiry.clientPortalEnabled ?? true,
          portalEnabled: inquiry.clientPortalEnabled ?? true,
          portal_token: portalToken,
          portalToken,
          // Seven checkpoints, same set the live intake path creates,
          // so the milestone rail isn't an empty strip on stage.
          milestones: [
            'Discovery Call',
            'Proposal Sent',
            'Contract Signed',
            'Pre-Production',
            'Production',
            'Post-Production',
            'Delivery',
          ].map((name, i) => ({
            id: `${projectId}-ms-${i + 1}`,
            project_id: projectId,
            projectId,
            name,
            completed: i === 0,
            sort_order: i + 1,
            sortOrder: i + 1,
          })),
          created_at: nowIso,
          createdAt: nowIso,
          updated_at: nowIso,
        });

        // Link the inquiry back, exactly like the live path, so the
        // modal flips to "Project Workspace Ready" and a second
        // conversion attempt is a no-op.
        const portalUrl = `/portal/${portalToken}`;
        demoStore.inquiries.update(inquiry.id, {
          projectId,
          project_id: projectId,
          portalUrl,
          portal_url: portalUrl,
          status: 'accepted',
          clientPortalEnabled: true,
          portal_enabled: true,
        } as any);

        return {
          projectId: project.id as string,
          portalToken,
          clientId: client.id as string,
        };
      }

      // HOTFIX-2 — the lead table is a MERGE of real inquiries and
      // tagged clients (Index.tsx:144 / NewDashboard.tsx:241). The
      // client-derived rows carry `client-<uuid>` ids and a hardcoded
      // status of 'accepted', so InquiryModal's `canConvert` lit the
      // "Convert to Project" button for every one of them — and every
      // click 400'd on the `.eq('id', 'client-…')` below, because
      // `inquiries.id` is a UUID column.
      //
      // Promote such a row into a real inquiry first (exactly what
      // InquiryModal.handleSave already does on Save), then let the
      // normal path convert it. Re-converting the same client finds
      // the promoted inquiry again and short-circuits on its
      // project_id, so this stays idempotent.
      let inquiryId = inquiry.id;
      // Set when we came in from a client row — we already know the
      // client, so we must NOT let the email-match below invent a
      // duplicate for a client that has no email on file.
      let knownClientId: string | null = null;

      if (isClientDerivedInquiryId(inquiry.id)) {
        const realClientId = inquiry.id.slice(CLIENT_DERIVED_PREFIX.length);

        const { data: clientRow, error: clientLookupError } = await supabase
          .from('clients')
          .select('id, org_id, name, email, phone, notes')
          .eq('id', realClientId)
          .maybeSingle();

        if (clientLookupError) throw clientLookupError;
        if (!clientRow) {
          throw new Error(
            "That client no longer exists — refresh the page and try again.",
          );
        }

        knownClientId = clientRow.id;

        // Reuse an inquiry we already promoted from this client so a
        // second conversion doesn't fork a second lead. The match key
        // is deliberately the SAME key the dashboards dedupe on
        // (`email|serviceType`) — if a matching inquiry existed, the
        // client row wouldn't be showing as a separate entry at all,
        // so the only thing this finds is our own earlier promotion.
        const serviceType = inquiry.serviceType || 'general';
        let promotedQuery = supabase
          .from('inquiries')
          .select('id')
          .eq('org_id', clientRow.org_id)
          .eq('service_type', serviceType);
        promotedQuery = clientRow.email
          ? promotedQuery.ilike('email', clientRow.email)
          : promotedQuery.eq('name', clientRow.name);

        const { data: alreadyPromoted, error: promotedLookupError } =
          await promotedQuery
            .order('submitted_at', { ascending: false })
            .limit(1);
        if (promotedLookupError) throw promotedLookupError;
        let promotedId: string | null = alreadyPromoted?.[0]?.id ?? null;

        if (!promotedId) {
          const { data: promoted, error: promoteError } = await supabase
            .from('inquiries')
            .insert({
              org_id: clientRow.org_id,
              name: clientRow.name,
              // `inquiries.email` is NOT NULL; a client without one
              // still deserves a lead record.
              email: clientRow.email ?? '',
              phone: clientRow.phone ?? null,
              status: 'accepted',
              stage: inquiry.projectStage || 'lead',
              service_type: serviceType,
              description: inquiry.projectDescription || null,
              notes: inquiry.additionalNotes ?? clientRow.notes ?? null,
              location: inquiry.location || null,
              estimated_completion: inquiry.estimatedCompletion || null,
            })
            .select('id')
            .single();

          if (promoteError) {
            debug.error(
              {
                action: 'promoteClientToInquiry',
                table: 'inquiries',
                orgId: clientRow.org_id,
                payload: { clientId: clientRow.id },
              },
              promoteError,
            );
            throw promoteError;
          }
          promotedId = promoted.id;
        }

        inquiryId = promotedId;
      }

      // Pull the latest inquiry row, including the project_id link if
      // it already exists. Public-form submissions create an inquiry
      // AND project pair atomically, so by the time the studio sees
      // it in the inbox the link is already set — converting again
      // would double-create. We treat conversion as idempotent: if
      // the pair exists, return its IDs and stop.
      //
      // POL-16 fix — `inquiries` has NO client_id column (types regen
      // Jul 31 confirms). The old select/update of inquiries.client_id
      // 400'd on the first query, which is why EVERY conversion failed
      // with "Failed to convert inquiry to project". The client link
      // lives on projects.client_id; client reuse is handled by the
      // org+email match below.
      const { data: inquiryData, error: inquiryError } = await supabase
        .from('inquiries')
        .select('org_id, email, project_id, status')
        .eq('id', inquiryId)
        .single();

      if (inquiryError) throw inquiryError;

      // Already converted → return existing ids, no inserts.
      if (inquiryData.project_id) {
        const { data: existingProject } = await supabase
          .from('projects')
          .select('id, portal_token, client_id')
          .eq('id', inquiryData.project_id)
          .single();
        if (existingProject) {
          // P2-1 — a converted lead must read 'accepted'. This branch
          // covers pairs created atomically by the public form (or a
          // conversion that predates this fix) where status was never
          // flipped, so the inbox kept saying "pending" about a lead
          // that already has a project.
          if (inquiryData.status !== 'accepted') {
            const { error: statusError } = await supabase
              .from('inquiries')
              .update({ status: 'accepted' })
              .eq('id', inquiryId);
            if (statusError) {
              debug.error(
                {
                  action: 'markInquiryAccepted',
                  table: 'inquiries',
                  payload: { inquiryId, projectId: existingProject.id },
                },
                statusError,
              );
              throw new Error(
                `The project already exists, but marking the lead accepted failed: ${debug.parseSupabaseError(
                  statusError,
                )}`,
              );
            }
          }
          return {
            projectId: existingProject.id,
            portalToken: existingProject.portal_token,
            clientId: existingProject.client_id,
          };
        }
        // project_id was set but the row vanished — fall through and
        // recreate. Rare, but covers a manually-deleted project.
      }

      // HOTFIX-2 — second idempotency net. The project insert and the
      // "write the link back onto the inquiry" update below are two
      // round trips; if the second one failed last time, the project
      // exists but `inquiries.project_id` is still null, and without
      // this check a retry would create a SECOND project. Anchor on
      // `projects.inquiry_id` (a real column — types.ts:4575) and
      // adopt the orphan instead of duplicating it.
      const { data: orphanProjects, error: orphanLookupError } = await supabase
        .from('projects')
        .select('id, portal_token, client_id')
        .eq('inquiry_id', inquiryId)
        .order('created_at', { ascending: true })
        .limit(1);
      if (orphanLookupError) throw orphanLookupError;

      const orphanProject = orphanProjects?.[0];
      if (orphanProject) {
        const orphanPortalUrl = orphanProject.portal_token
          ? `/portal/${orphanProject.portal_token}`
          : `/portal/${orphanProject.id}`;
        // P2-1 — adopting the orphan IS the conversion succeeding, so
        // the lead flips to 'accepted' here too.
        const { error: relinkError } = await supabase
          .from('inquiries')
          .update({
            project_id: orphanProject.id,
            portal_url: orphanPortalUrl,
            status: 'accepted',
          })
          .eq('id', inquiryId);
        if (relinkError) throw relinkError;

        return {
          projectId: orphanProject.id,
          portalToken: orphanProject.portal_token,
          clientId: orphanProject.client_id,
        };
      }

      // Reuse an existing client when possible (same org + same email)
      // so a returning lead doesn't get duplicated. Falls through to
      // insert when no match exists or the inquiry has no email.
      //
      // HOTFIX-2 — when we came in from a tagged client we already
      // hold its id; skip the email match entirely so a client with
      // no email on file doesn't get a duplicate record created.
      let clientId: string | null = knownClientId;
      if (!clientId && inquiry.email) {
        const { data: existingClient } = await supabase
          .from('clients')
          .select('id')
          .eq('org_id', inquiryData.org_id)
          .ilike('email', inquiry.email)
          .maybeSingle();
        clientId = existingClient?.id ?? null;
      }

      if (!clientId) {
        const { data: client, error: clientError } = await supabase
          .from('clients')
          .insert({
            name: inquiry.name,
            email: inquiry.email,
            phone: inquiry.phone || null,
            org_id: inquiryData.org_id,
            notes: inquiry.additionalNotes || null,
            created_at: inquiry.submittedAt || new Date().toISOString(),
          })
          .select()
          .single();
        if (clientError) throw clientError;
        clientId = client.id;
      }

      // Create the project. If this insert fails after a NEW client
      // was inserted above, the orphan client is preserved so the
      // next conversion attempt can reuse it via the email match.
      //
      // HOTFIX-2 — every key below is a real `projects` column
      // (migration 20251206203951 + types.ts:4566 Insert): name,
      // description, org_id, client_id, inquiry_id, stage,
      // portal_enabled, due_date, location, notes. `stage` must be a
      // `project_stage` member and `status` (left to its DB default of
      // 'discovery') a `project_status` member — they are SEPARATE
      // enums, so never put a stage value in `status`. One unknown key
      // or one bad enum member makes PostgREST reject the whole row.
      //
      // `inquiry_id` is written here on purpose: it's what the
      // orphan-adoption lookup above keys off, so a failure on the
      // link-back update below is recoverable rather than duplicating.
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({
          name: inquiry.projectDescription || `${inquiry.name} Project`,
          description: inquiry.projectDescription || null,
          org_id: inquiryData.org_id,
          client_id: clientId,
          inquiry_id: inquiryId,
          stage: inquiry.projectStage || 'lead',
          portal_enabled: inquiry.clientPortalEnabled ?? false,
          due_date: inquiry.estimatedCompletion || null,
          location: inquiry.location || null,
          notes: inquiry.additionalNotes || null,
        })
        .select()
        .single();

      if (projectError) {
        debug.error(
          {
            action: 'convertInquiryToProject',
            table: 'projects',
            orgId: inquiryData.org_id,
            payload: { inquiryId, clientId },
          },
          projectError,
        );
        throw projectError;
      }

      // Update the inquiry with the project link. portal_token is
      // generated by a DB default; if it's null, store the project id
      // path instead so the link still points somewhere meaningful.
      const portalUrl = project.portal_token
        ? `/portal/${project.portal_token}`
        : `/portal/${project.id}`;
      // P2-1 — the demo branch and the client-promotion insert both
      // already set 'accepted'; the plain live conversion never did,
      // so a converted lead kept reading "pending" in the inbox. Flip
      // it in the same write as the project link.
      const { error: updateError } = await supabase
        .from('inquiries')
        .update({
          project_id: project.id,
          portal_url: portalUrl,
          status: 'accepted',
        })
        .eq('id', inquiryId);

      // HOTFIX-2 — the project DID get created at this point. Throwing
      // is still right (the caller must not navigate as if everything
      // worked), but the message has to say the project exists so the
      // studio doesn't go hunting for a workspace that's actually
      // there. A retry adopts it via `projects.inquiry_id` above
      // rather than creating a second one.
      if (updateError) {
        debug.error(
          {
            action: 'linkInquiryToProject',
            table: 'inquiries',
            payload: { inquiryId, projectId: project.id },
          },
          updateError,
        );
        throw new Error(
          `The project was created, but linking it back to the lead failed: ${debug.parseSupabaseError(
            updateError,
          )} Open it from Projects, or convert again to re-link it.`,
        );
      }

      return {
        projectId: project.id,
        portalToken: project.portal_token,
        clientId: clientId,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // DEMO-4 — ['projects'] does NOT partial-match ['project', id],
      // which is the key ProjectWorkspace reads. Without this the
      // freshly-converted project could render from a cold cache the
      // moment we navigate to /studio/:id.
      queryClient.invalidateQueries({ queryKey: ['project'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
};

// Stats hooks that use the inquiry data
export const useInquiryStats = (serviceType?: ServiceType): StatsData => {
  const { data: inquiries = [] } = useInquiries(serviceType);
  // We compute stats directly from the live `inquiries` array — when
  // isDemo is on, those rows are already the demoOverlay set, so the
  // stats line up with the table beneath.
  return {
    total: inquiries.length,
    pending: inquiries.filter((i: any) => i.status === 'pending').length,
    accepted: inquiries.filter((i: any) => i.status === 'accepted').length,
    followUp: inquiries.filter((i: any) => i.status === 'follow_up').length,
    activePortals: inquiries.filter((i: any) => i.clientPortalEnabled || i.portal_enabled).length,
  };
};

export const useAnalyticsData = (): AnalyticsData => {
  const { data: inquiries = [] } = useInquiries();

  // DEMO-4 — this used to short-circuit to `getAnalyticsData()` in
  // mockData.ts, which computed its acceptance rate from the 2024
  // `mockInquiries` set and hardcoded "156 all time / 24 this month".
  // The generic path below already reads whatever `useInquiries`
  // returned — which under Preview Studio IS the demo cast — so demo
  // and live now agree with the table on screen.
  // P2-1 — this was `inquiries.length || 1`, which existed only to
  // dodge a divide-by-zero in acceptanceRate but also made
  // "Total all time" read 1 for a studio with ZERO inquiries. Guard
  // the division, report the count honestly.
  const total = inquiries.length;
  const accepted = inquiries.filter(i =>
    i.status === 'accepted' || i.status === 'in_progress' || i.status === 'completed'
  ).length;

  return {
    totalAllTime: total,
    thisMonth: inquiries.filter(i => {
      const submitted = new Date(i.submittedAt);
      const now = new Date();
      return submitted.getMonth() === now.getMonth() && submitted.getFullYear() === now.getFullYear();
    }).length,
    acceptanceRate: total > 0 ? Math.round((accepted / total) * 100) : 0,
    inProgress: inquiries.filter(i => i.status === 'in_progress').length,
  };
};

export const useServiceSummary = (serviceType: ServiceType): ServiceSummary => {
  const { data: inquiries = [] } = useInquiries(serviceType);

  // DEMO-4 — the demo short-circuit called `getServiceSummary()`,
  // which padded its counts with `Math.floor(Math.random() * 20)`.
  // That made the same number change between renders mid-demo. Gone;
  // both modes now count the rows actually on screen.
  return {
    total: inquiries.length,
    accepted: inquiries.filter(i => i.status === 'accepted' || i.status === 'completed').length,
    inProgress: inquiries.filter(i => i.status === 'in_progress').length,
  };
};
